import { useMemo } from 'react';
import { toDate } from '../utils/localeFormatters';

/**
 * Derive actionable dashboard insights from already-fetched data.
 *
 * No new network calls. All signals are computed from the arrays the
 * dashboard already has in memory (students, lessons, activities,
 * surveys, payments). Each section degrades gracefully when the
 * underlying data is missing.
 */
export default function useDashboardInsights({
  students = [],
  lessons = [],
  activities = [],
  surveys = [],
  payments = [],
  attendanceWindowDays = 28,
  attendanceThreshold = 0.7,
} = {}) {
  return useMemo(() => {
    const now = new Date();
    const windowStart = new Date(now);
    windowStart.setDate(windowStart.getDate() - attendanceWindowDays);

    /* ---------- low-attendance students ---------- */
    // Prefer explicit `attendanceRate` on the student, otherwise derive
    // a rough rate from audit-log attendance events in the window.
    const attendanceEvents = activities.filter(
      (a) => a && (a.action === 'attendance_marked' || a.entityType === 'attendance'),
    );
    const presenceByStudent = new Map();
    for (const ev of attendanceEvents) {
      const when = toDate(ev.timestamp);
      if (!when || when < windowStart) continue;
      const sid = ev.studentId || ev.entityId;
      if (!sid) continue;
      const bucket = presenceByStudent.get(sid) || { present: 0, total: 0 };
      bucket.total += 1;
      if (ev.result === 'present' || ev.status === 'present') bucket.present += 1;
      presenceByStudent.set(sid, bucket);
    }

    const lowAttendance = students
      .map((s) => {
        const explicit = typeof s.attendanceRate === 'number' ? s.attendanceRate : null;
        const derived = presenceByStudent.get(s.id);
        const rate =
          explicit ??
          (derived && derived.total >= 3 ? derived.present / derived.total : null);
        return rate == null ? null : { student: s, rate };
      })
      .filter((r) => r && r.rate < attendanceThreshold)
      .sort((a, b) => a.rate - b.rate)
      .slice(0, 5);

    /* ---------- overdue payments ---------- */
    const overdue = (payments || [])
      .filter((p) => {
        if (!p) return false;
        if (p.status === 'paid' || p.paidAt) return false;
        const due = toDate(p.dueDate || p.scheduledDate);
        return due && due < now;
      })
      .map((p) => ({
        id: p.id,
        amount: Number(p.amount) || 0,
        dueDate: toDate(p.dueDate || p.scheduledDate),
        studentId: p.studentId,
      }));
    const overdueTotal = overdue.reduce((s, p) => s + p.amount, 0);

    /* ---------- active surveys ---------- */
    const activeSurveys = (surveys || []).filter((s) => s && s.isActive !== false);

    /* ---------- weekly attendance rate series (last 6 weeks) ---------- */
    const weeks = [];
    for (let i = 5; i >= 0; i--) {
      const weekEnd = new Date(now);
      weekEnd.setDate(weekEnd.getDate() - i * 7);
      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekStart.getDate() - 6);
      let present = 0;
      let total = 0;
      for (const ev of attendanceEvents) {
        const when = toDate(ev.timestamp);
        if (!when || when < weekStart || when > weekEnd) continue;
        total += 1;
        if (ev.result === 'present' || ev.status === 'present') present += 1;
      }
      // Fallback: use lessons-held as a proxy if no attendance events logged.
      if (total === 0) {
        const held = lessons.filter((l) => {
          const d = toDate(l.scheduledDate || l.date);
          return d && d >= weekStart && d <= weekEnd;
        }).length;
        weeks.push({ weekStart, rate: held > 0 ? 1 : 0, proxy: true });
      } else {
        weeks.push({ weekStart, rate: present / total, proxy: false });
      }
    }

    /* ---------- weekly enrollment counts ---------- */
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(thisWeekStart.getDate() - 7);
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const newThisWeek = students.filter((s) => {
      const d = toDate(s.createdAt);
      return d && d >= thisWeekStart;
    }).length;
    const newLastWeek = students.filter((s) => {
      const d = toDate(s.createdAt);
      return d && d >= lastWeekStart && d < thisWeekStart;
    }).length;

    const currentRate = weeks[weeks.length - 1]?.rate ?? null;
    const prevRate = weeks[weeks.length - 2]?.rate ?? null;
    const weeklyAttendanceDelta =
      currentRate != null && prevRate != null ? currentRate - prevRate : null;

    return {
      lowAttendance,
      overdue,
      overdueTotal,
      activeSurveys,
      attendanceSeries: weeks,
      currentAttendanceRate: currentRate,
      weeklyAttendanceDelta,
      newThisWeek,
      newLastWeek,
      hasInsights:
        lowAttendance.length > 0 || overdue.length > 0 || activeSurveys.length > 0,
    };
  }, [students, lessons, activities, surveys, payments, attendanceWindowDays, attendanceThreshold]);
}
